/**
 * P-285 · which tasks are selected — the whole model, as plain functions.
 *
 * The user's ask was *"let me select multiple tasks and add also a button Select all"*, and the
 * reason this is a module rather than a `Record<string, boolean>` inside `TasksPage` (which is
 * what round T4 shipped) is that "selected" stopped being one boolean per row the moment ranges,
 * a tri-state header and a per-group box arrived: a Shift range needs an **anchor**, the header
 * needs to know whether *every loaded* row is in, and a group box needs the same answer over its
 * own rows. Three call sites asking a `Record` those questions is three chances to disagree.
 *
 * The shape is the grid's (`board-view/table/selection.ts` — `ItemSelection`), deliberately:
 * the two surfaces now do the same gesture, and the Tasks page's bulk bar is the grid's bulk bar
 * with two actions. What is **not** shared is the module itself — the grid's is typed on
 * `ItemId` (a branded bigint) over one board, and a task selection is a list of `TaskRow.id`
 * strings spanning boards.
 *
 * Two orders exist here and they are not the same list, which is the one subtlety:
 *
 *   · **loaded order** — every row of every page walked so far, which is what "Select all" and
 *     the header's tri-state mean (`mergeTaskPages(pages).rows`);
 *   · **visible order** — the rows actually painted, in paint order, which is what a Shift range
 *     runs along. A collapsed bucket's rows are loaded and not visible, and a range that jumped
 *     through them would select rows the person cannot see.
 *
 * `ids` is always kept in the order it was given, so `selectedRows` comes out in paint order and
 * the bulk bar's count never depends on the order the boxes were ticked in.
 */
import type { TaskGroupView } from './types';

export interface TaskSelection {
  /** Selected `TaskRow.id`s, in the order of the list they were selected from. */
  readonly ids: readonly string[];
  /** The row a Shift+click measures from — the **last toggled** row, never a range end. */
  readonly anchor: string | null;
}

export const EMPTY_TASK_SELECTION: TaskSelection = { ids: [], anchor: null };

/** What a tri-state checkbox shows: unticked · dash · ticked. */
export type TaskSelectState = 'none' | 'some' | 'all';

export function taskSelectionSet(selection: TaskSelection): Set<string> {
  return new Set(selection.ids);
}

/**
 * De-duplicates and sorts ids into `order` — **keeping** the ones the order does not contain,
 * at the end and in the order they came.
 *
 * Keeping them is the point. The order a toggle is given is the *visible* one, and a person who
 * ticks three rows in Later and then collapses Later still has three tasks selected: dropping
 * what the current order cannot place would have quietly emptied the bulk bar at the moment a
 * bucket was shut. `Array#sort` has been stable since ES2019, so the unplaceable ids keep their
 * relative order rather than being shuffled.
 */
function inOrder(order: readonly string[], ids: Iterable<string>): string[] {
  const rank = new Map(order.map((id, at) => [id, at]));
  const out: string[] = [];
  const seen = new Set<string>();
  for (const id of ids) {
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out.sort(
    (a, b) => (rank.get(a) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b) ?? Number.MAX_SAFE_INTEGER),
  );
}

/** Exactly the ids the order contains, in its order — a selection *taken from* one list. */
function onlyInOrder(order: readonly string[], ids: Iterable<string>): string[] {
  const wanted = new Set(ids);
  return order.filter((id) => wanted.has(id));
}

/**
 * A plain click on a row's checkbox: in or out, and the anchor moves here.
 *
 * The anchor moves on **every** toggle, including an untick, because that is what the next
 * Shift+click should measure from — the row the person last touched.
 */
export function toggleTask(
  selection: TaskSelection,
  order: readonly string[],
  id: string,
): TaskSelection {
  const has = selection.ids.includes(id);
  const next = has
    ? selection.ids.filter((entry) => entry !== id)
    : inOrder(order, [...selection.ids, id]);
  return { ids: next, anchor: id };
}

/**
 * Shift+click — the inclusive range from the anchor to this row, in **visible** order.
 *
 * It **adds**; it never unselects what was already in. That is the grid's rule (`extendItemRange`)
 * and the one every list in this product follows, so a person who ticked three rows here and
 * Shift-clicked a fourth there keeps all four. With no anchor (nothing toggled yet) it is a plain
 * toggle, and so is a row the visible order does not contain — a row inside a collapsed bucket
 * cannot be a range end.
 */
export function extendTaskRange(
  selection: TaskSelection,
  order: readonly string[],
  id: string,
): TaskSelection {
  if (selection.anchor === null) return toggleTask(selection, order, id);
  const from = order.indexOf(selection.anchor);
  const to = order.indexOf(id);
  if (from < 0 || to < 0) return toggleTask(selection, order, id);
  const slice = order.slice(Math.min(from, to), Math.max(from, to) + 1);
  return {
    ids: inOrder(order, [...selection.ids, ...slice]),
    /* The anchor stays put, so dragging the range back and forth keeps measuring from it. */
    anchor: selection.anchor,
  };
}

/** Exactly these rows, in this order — what "Select all N in this tab" ends on. */
export function selectTasks(order: readonly string[], ids: readonly string[]): TaskSelection {
  const picked = onlyInOrder(order, ids);
  return { ids: picked, anchor: picked[picked.length - 1] ?? null };
}

/**
 * **Select all loaded** — the header checkbox, the toolbar button and `Ctrl/Cmd+A`.
 *
 * "Loaded", not "in the tab": the page holds the pages it has walked, and a button that silently
 * reached rows nobody has seen would make the count in the bulk bar a number the list cannot
 * show. When more pages exist the bulk bar offers the other half of the gesture explicitly
 * ("Select all M in this tab"), which is monday's own pattern and every mail client's.
 */
export function selectAllTasks(order: readonly string[]): TaskSelection {
  return { ids: [...order], anchor: order[order.length - 1] ?? null };
}

/** One bucket's box: add every row of the group, or take every row of it out. */
export function setTaskGroupSelected(
  selection: TaskSelection,
  order: readonly string[],
  groupIds: readonly string[],
  selected: boolean,
): TaskSelection {
  if (selected) {
    return {
      ids: inOrder(order, [...selection.ids, ...groupIds]),
      anchor: groupIds[groupIds.length - 1] ?? selection.anchor,
    };
  }
  const dropped = new Set(groupIds);
  const ids = selection.ids.filter((id) => !dropped.has(id));
  return { ids, anchor: selection.anchor !== null && dropped.has(selection.anchor) ? null : selection.anchor };
}

/**
 * The tri-state of a checkbox that speaks for `ids` — the header (every loaded row) or a group
 * header (that bucket's rows).
 *
 * An **empty** list is `none`, never `all`: a header that ticked itself over no rows would say
 * the list is fully selected while the bulk bar said nothing is.
 */
export function taskSelectState(selection: TaskSelection, ids: readonly string[]): TaskSelectState {
  if (ids.length === 0) return 'none';
  const chosen = taskSelectionSet(selection);
  let hit = 0;
  for (const id of ids) if (chosen.has(id)) hit += 1;
  if (hit === 0) return 'none';
  return hit === ids.length ? 'all' : 'some';
}

/**
 * The selection after the list answered again — a sort, a re-grouping, a realtime repaint.
 *
 * A row that is still there stays selected (the card: *"selection survives sort/group changes
 * for rows still present"*); one that the new answer does not carry is dropped rather than kept
 * as a ghost the bulk bar would count and no action could write. The **same object** comes back
 * when nothing changed, so this can be called from an effect without looping.
 */
export function keepSelectedTasks(selection: TaskSelection, ids: readonly string[]): TaskSelection {
  if (selection.ids.length === 0) return selection;
  const present = new Set(ids);
  const kept = selection.ids.filter((id) => present.has(id));
  const anchor = selection.anchor !== null && present.has(selection.anchor) ? selection.anchor : null;
  if (kept.length === selection.ids.length && anchor === selection.anchor) return selection;
  return { ids: kept, anchor };
}

/**
 * The rows in **paint** order: every expanded bucket's rows, bucket by bucket.
 *
 * `collapsed` is the page's own `Record<groupKey, boolean>`; a bucket that is shut contributes
 * nothing, because a range must not run through rows that are not on screen.
 */
export function visibleTaskOrder(
  groups: readonly TaskGroupView[],
  collapsed: Readonly<Record<string, boolean>> = {},
): string[] {
  const out: string[] = [];
  for (const group of groups) {
    if (collapsed[group.key] === true) continue;
    for (const row of group.rows) out.push(row.id);
  }
  return out;
}

/** "3 selected" — the bulk bar's count line, one place so the two callers cannot drift. */
export function describeTaskSelection(count: number): string {
  return `${count} selected`;
}
